/**
 * AccountSettings — preferences, app settings and account actions.
 *
 * Every control edits a local draft; one Save posts the batch. The avatar is
 * the exception and saves immediately, like mobile.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import accountService from '@wlb/shared/api/services/accountService';
import {
  PROFILE_VISIBILITY_ID,
  WEIGHT_VISIBILITY_ID,
  profileVisibilityKeyFromId,
  weightVisibilityKeyFromId,
  type ProfileVisibilityKey,
  type WeightVisibilityKey,
} from '@wlb/shared/constants/accountSettings';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useAccountSettings } from '@wlb/shared/hooks/useAccountSettings';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { WEIGHT_SCALE_UNIT_ID } from '@wlb/shared/utils/weightUtils';
import type { ApiError } from '@wlb/shared/types';
import { APP_ENVIRONMENT } from '../config/environment';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Modal } from '../components/ui/Modal';
import { Switch } from '../components/ui/Switch';
import {
  BellIcon,
  CameraIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  LogoutIcon,
  MailIcon,
  PostVideoIcon,
  ShareIcon,
  TrashIcon,
} from '../components/ui/icons';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { AvatarUploadModal } from '../features/profile/AvatarUploadModal';
import { getProfileAvatarUrl, useCurrentProfile } from '../features/profile/useCurrentProfile';
import '../features/settings/settings.css';

type VisibilityTarget = 'profile' | 'weight';
type Dialog = 'avatar' | 'logout' | 'delete' | null;

const PROFILE_VISIBILITY_KEYS: ProfileVisibilityKey[] = ['public', 'buddies', 'private'];
const WEIGHT_VISIBILITY_KEYS: WeightVisibilityKey[] = ['public', 'buddies', 'onlyMe'];

/** i18n suffix for each visibility option; the two lists share the copy shape. */
const VISIBILITY_LABEL_KEY: Record<ProfileVisibilityKey | WeightVisibilityKey, string> = {
  public: 'public',
  buddies: 'buddies',
  private: 'private',
  onlyMe: 'private',
};

const APP_SETTINGS = [
  ['notificationsEnabled', 'notifications', BellIcon],
  ['videosStartWithSound', 'videosSound', PostVideoIcon],
  ['allowSharing', 'allowSharing', ShareIcon],
] as const;

export function AccountSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const profile = useCurrentProfile();
  const { settings, loading, saving, error, hasChanges, setField, reload, save } =
    useAccountSettings();

  const [dialog, setDialog] = useState<Dialog>(null);
  const [visibilityTarget, setVisibilityTarget] = useState<VisibilityTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    const result = await save();
    ToastService.show(
      result.success
        ? { type: 'success', message: t('accountSettings.toast.saved') }
        : { type: 'error', message: result.message || t('common.errors.generic') },
    );
  };

  const handleLogout = async () => {
    setDialog(null);
    navigate('/', { replace: true });
    await logout();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await accountService.deleteMyAccount();
      setDialog(null);
      navigate('/', { replace: true });
      await logout();
    } catch (caught) {
      ToastService.show({ type: 'error', message: getApiErrorMessage(caught as ApiError) });
    } finally {
      setDeleting(false);
    }
  };

  const profileVisibility = profileVisibilityKeyFromId(settings?.profileViewPrivacy);
  const weightVisibility = weightVisibilityKeyFromId(settings?.weightPrivacy);
  const isLbs = settings?.weightScaleUnit === WEIGHT_SCALE_UNIT_ID.LBS;
  const isOnlineVisible = settings?.isOnlineVisible ?? true;

  const visibilityLabel = (target: VisibilityTarget) =>
    t(
      `accountSettings.visibility.${target}.${
        VISIBILITY_LABEL_KEY[target === 'profile' ? profileVisibility : weightVisibility]
      }Title`,
    );

  const renderVisibilityRow = (target: VisibilityTarget) => (
    <button
      type="button"
      className="settings-row settings-row--button"
      onClick={() => setVisibilityTarget(target)}
    >
      <span className="settings-row__copy">
        <span className="settings-row__title">
          {t(
            target === 'profile'
              ? 'accountSettings.preferences.profileVisibility'
              : 'accountSettings.preferences.weightVisibility',
          )}
        </span>
        <span className="settings-row__description">
          {t('accountSettings.preferences.tapToChange')}
        </span>
      </span>
      <span className="settings-row__value">{visibilityLabel(target)}</span>
      <ChevronRightIcon size={18} />
    </button>
  );

  const renderSegmented = (
    label: string,
    options: Array<{ key: string; label: string; active: boolean; onSelect: () => void }>,
  ) => (
    <div className="settings-segmented" role="group" aria-label={label}>
      {options.map(option => (
        <button
          key={option.key}
          type="button"
          aria-pressed={option.active}
          className={`settings-segmented__option${option.active ? ' settings-segmented__option--active' : ''}`}
          onClick={option.onSelect}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const renderBody = () => {
    if (loading && !settings) return <MoreLoading variant="card" />;
    if (!settings) {
      return (
        <ListStatus
          variant="error"
          message={error || t('common.errors.generic')}
          onAction={reload}
          busy={loading}
        />
      );
    }

    return (
      <>
        <div className="settings-profile">
          <button
            type="button"
            className="settings-profile__avatar"
            aria-label={t('accountSettings.accessibility.changePhoto')}
            onClick={() => setDialog('avatar')}
          >
            <Avatar
              src={getProfileAvatarUrl(profile)}
              name={profile?.name}
              username={profile?.userName}
              seed={profile?.userGuid ?? profile?.userId}
              size="xl"
            />
            <span className="settings-profile__badge" aria-hidden>
              <CameraIcon size={12} />
            </span>
          </button>
          <div className="settings-profile__copy">
            <p className="settings-profile__name">{profile?.name || ''}</p>
            <p className="settings-profile__email">
              <MailIcon size={14} aria-hidden />
              {profile?.email || ''}
            </p>
          </div>
        </div>

        <section className="settings-section" aria-labelledby="preferences-title">
          <h2 className="settings-section__title" id="preferences-title">
            {t('accountSettings.sections.preferences')}
          </h2>
          <div className="settings-card">
            <div className="settings-row">
              <span className="settings-row__copy">
                <span className="settings-row__title">
                  {t('accountSettings.preferences.unit')}
                </span>
              </span>
              {renderSegmented(t('accountSettings.preferences.unit'), [
                {
                  key: 'kg',
                  label: t('accountSettings.preferences.kg'),
                  active: !isLbs,
                  onSelect: () => setField('weightScaleUnit', WEIGHT_SCALE_UNIT_ID.KG),
                },
                {
                  key: 'lbs',
                  label: t('accountSettings.preferences.lbs'),
                  active: isLbs,
                  onSelect: () => setField('weightScaleUnit', WEIGHT_SCALE_UNIT_ID.LBS),
                },
              ])}
            </div>
            {renderVisibilityRow('profile')}
            {renderVisibilityRow('weight')}
            <div className="settings-row">
              <span className="settings-row__copy">
                <span className="settings-row__title">
                  {t('accountSettings.preferences.onlineVisibility')}
                </span>
              </span>
              {renderSegmented(t('accountSettings.preferences.onlineVisibility'), [
                {
                  key: 'off',
                  label: t('accountSettings.preferences.off'),
                  active: !isOnlineVisible,
                  onSelect: () => setField('isOnlineVisible', false),
                },
                {
                  key: 'on',
                  label: t('accountSettings.preferences.on'),
                  active: isOnlineVisible,
                  onSelect: () => setField('isOnlineVisible', true),
                },
              ])}
            </div>
          </div>
        </section>

        <section className="settings-section" aria-labelledby="app-settings-title">
          <h2 className="settings-section__title" id="app-settings-title">
            {t('accountSettings.sections.appSettings')}
          </h2>
          <div className="settings-card">
            {APP_SETTINGS.map(([field, labelKey, Icon]) => (
              <div className="settings-row" key={field}>
                <span className="settings-row__icon">
                  <Icon size={18} />
                </span>
                <span className="settings-row__copy">
                  <span className="settings-row__title" id={`app-setting-${field}`}>
                    {t(`accountSettings.appSettings.${labelKey}`)}
                  </span>
                  <span className="settings-row__description">
                    {t(`accountSettings.appSettings.${labelKey}Desc`)}
                  </span>
                </span>
                <Switch
                  checked={settings[field] ?? true}
                  onChange={value => setField(field, value)}
                  label={t(`accountSettings.appSettings.${labelKey}`)}
                  labelledBy={`app-setting-${field}`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="account-actions-title">
          <h2 className="settings-section__title" id="account-actions-title">
            {t('accountSettings.sections.accountActions')}
          </h2>
          <div className="settings-card">
            <button
              type="button"
              className="settings-row settings-row--button"
              onClick={() => setDialog('logout')}
            >
              <span className="settings-row__icon">
                <LogoutIcon size={18} />
              </span>
              <span className="settings-row__copy">
                <span className="settings-row__title">{t('accountSettings.actions.logout')}</span>
                <span className="settings-row__description">
                  {t('accountSettings.actions.logoutDesc')}
                </span>
              </span>
              <ChevronRightIcon size={18} />
            </button>
            <button
              type="button"
              className="settings-row settings-row--button settings-row--danger"
              onClick={() => setDialog('delete')}
            >
              <span className="settings-row__icon">
                <TrashIcon size={18} />
              </span>
              <span className="settings-row__copy">
                <span className="settings-row__title">
                  {t('accountSettings.actions.deleteAccount')}
                </span>
                <span className="settings-row__description">
                  {t('accountSettings.actions.deleteAccountDesc')}
                </span>
              </span>
              <ChevronRightIcon size={18} />
            </button>
          </div>
        </section>

        <p className="settings-meta">
          {t('accountSettings.appInfo.environment', { environment: APP_ENVIRONMENT })}
        </p>

        <div className="settings-savebar">
          <Button
            fullWidth
            disabled={!hasChanges || saving}
            onClick={() => { handleSave().catch(() => undefined); }}
          >
            {t(saving ? 'common.buttons.saving' : 'common.buttons.save')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <MorePage title={t('accountSettings.title')}>
      {renderBody()}

      {visibilityTarget && settings && (
        <Modal
          title={t(`accountSettings.visibility.${visibilityTarget}.title`)}
          labelledBy="visibility-title"
          onClose={() => setVisibilityTarget(null)}
        >
          <div className="settings-confirm">
            <p className="settings-confirm__message">
              {t(`accountSettings.visibility.${visibilityTarget}.chooseAudience`)}
            </p>
            <ul className="settings-options">
              {(visibilityTarget === 'profile'
                ? PROFILE_VISIBILITY_KEYS
                : WEIGHT_VISIBILITY_KEYS
              ).map(key => {
                const selected =
                  visibilityTarget === 'profile'
                    ? profileVisibility === key
                    : weightVisibility === key;
                const labelKey = VISIBILITY_LABEL_KEY[key];
                return (
                  <li key={key}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      className={`settings-option${selected ? ' settings-option--selected' : ''}`}
                      onClick={() => {
                        if (visibilityTarget === 'profile') {
                          setField(
                            'profileViewPrivacy',
                            PROFILE_VISIBILITY_ID[key as ProfileVisibilityKey],
                          );
                        } else {
                          setField(
                            'weightPrivacy',
                            WEIGHT_VISIBILITY_ID[key as WeightVisibilityKey],
                          );
                        }
                        setVisibilityTarget(null);
                      }}
                    >
                      <span className="settings-option__copy">
                        <span className="settings-option__title">
                          {t(`accountSettings.visibility.${visibilityTarget}.${labelKey}Title`)}
                        </span>
                        <span className="settings-option__description">
                          {t(`accountSettings.visibility.${visibilityTarget}.${labelKey}Desc`)}
                        </span>
                      </span>
                      {selected && (
                        <CheckCircleIcon size={20} className="settings-option__check" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      )}

      {dialog === 'avatar' && (
        <AvatarUploadModal
          currentAvatarUrl={getProfileAvatarUrl(profile)}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === 'logout' && (
        <ConfirmModal
          title={t('accountSettings.logoutConfirm.title')}
          message={t('accountSettings.logoutConfirm.message')}
          confirmLabel={t('accountSettings.logoutConfirm.yes')}
          cancelLabel={t('accountSettings.logoutConfirm.no')}
          showIcon
          onClose={() => setDialog(null)}
          onConfirm={() => { handleLogout().catch(() => undefined); }}
        />
      )}

      {dialog === 'delete' && (
        <ConfirmModal
          title={t('accountSettings.deleteConfirm.title')}
          message={t('accountSettings.deleteConfirm.message')}
          confirmLabel={t('accountSettings.deleteConfirm.yes')}
          cancelLabel={t('accountSettings.deleteConfirm.no')}
          showIcon
          loading={deleting}
          onClose={() => setDialog(null)}
          onConfirm={() => { handleDelete().catch(() => undefined); }}
        />
      )}
    </MorePage>
  );
}
